import type { Message } from '../index';

import { VoteType } from '@readme/iso';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useDarkMode } from '@Hub/Header/utils/hooks';

import useClassy from '@core/hooks/useClassy';
import { classy } from '@core/utils/classy';

import RDMD from '@ui/RDMD';
import Tooltip from '@ui/Tooltip';

import classes from './ChatMessage.module.scss';
import TypingIndicator from './TypingIndicator';

interface ChatMessageProps {
  message: Message;
  onVote: (messageId: string, voteType: VoteType) => void;
  showTypingIndicator?: boolean;
}

export default function ChatMessage({ message, onVote, showTypingIndicator }: ChatMessageProps) {
  const { t } = useTranslation();
  const bem = useClassy(classes, 'ChatMessage');
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'ai';
  const colorScheme = useDarkMode();

  return (
    <div className={classy(bem('&', isUser && '_user', isAssistant && '_assistant'), 'rm-AskAi-message')}>
      <div className={bem('-content')}>
        {message.content ? (
          isUser ? (
            // Render user messages as simple text
            <div className={bem('-userMessage')}>{message.content}</div>
          ) : (
            // Render assistant messages with markdown
            <div className={bem('-assistantMessage')}>
              <RDMD className={bem('-markdownContent')} format="md" opts={{ theme: colorScheme }}>
                {message.content}
              </RDMD>
              {!!onVote && !message.isLoading && (
                <div className={bem('-voteButtons')}>
                  {[
                    { type: VoteType.UP as const, icon: 'icon-thumbs-up-2', label: t('owlbotChat.voteLabelUp') },
                    { type: VoteType.DOWN as const, icon: 'icon-thumbs-down1', label: t('owlbotChat.voteLabelDown') },
                  ].map(({ type, icon, label }) => {
                    const isSelected = message.vote === type;
                    return (
                      <Tooltip key={type} asTitle content={label}>
                        <button
                          aria-label={label}
                          aria-pressed={isSelected}
                          className={bem('-voteButton', isSelected && 'vote')}
                          onClick={() => onVote(message.id, type)}
                          type="button"
                        >
                          <i aria-hidden="true" className={icon} />
                        </button>
                      </Tooltip>
                    );
                  })}
                </div>
              )}
            </div>
          )
        ) : (
          // Show typing indicator when there's no content yet
          showTypingIndicator && <TypingIndicator />
        )}
      </div>
    </div>
  );
}
